import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// dialog-specific UI moved to EditTeamDialog
import EditTeamDialog from '@/components/dialogs/EditTeamDialog';
// moved to EditTeamDialog
import { toast } from 'sonner';
import { teamService, type TeamDetailsResponse } from '@/services/teamService';
import { useAuth } from '@/providers/AuthProvider';
import { TeamMembersCard, InviteMemberDialog } from '@/components/team';
import { Skeleton } from '@/components/ui/skeleton';
import type { PageProps } from '@/types/page';
import type { TeamMemberRole } from '@arrowise/shared';
import { ArrowLeft, Settings, Info } from 'lucide-react';
import UpdateTeamKeyModeDialog from '@/components/team/UpdateTeamKeyModeDialog';
import { Badge } from '@/components/ui/badge';
import type { TeamKeyMode } from '@arrowise/shared';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export const ManageTeamPage: React.FC<PageProps> = ({ nav = true, footer = true }) => {
    const { user } = useAuth();
    const { id } = useParams<{ id: string }>();
    const [teamDetails, setTeamDetails] = useState<TeamDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState<TeamMemberRole | null>(null);

    // Edit team dialog state
    const [editTeamOpen, setEditTeamOpen] = useState(false);
    const [teamName, setTeamName] = useState('');
    const [teamDescription, setTeamDescription] = useState('');
    const [updatingTeam, setUpdatingTeam] = useState(false);
    const [keyModeDialogOpen, setKeyModeDialogOpen] = useState(false);

    // Invite member dialog state
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<TeamMemberRole>('member');
    const [inviting, setInviting] = useState(false);

    useEffect(() => {
        if (id) {
            loadTeamDetails();
        }
    }, [id]);

    const loadTeamDetails = async () => {
        if (!id) return;

        try {
            setLoading(true);
            const details = await teamService.getTeamDetailsById(id);
            setTeamDetails(details);
            setUserRole(details.userMembership.role);
            setTeamName(details.team.name);
            setTeamDescription(details.team.description || '');
            // ensure we refresh details after key mode change
        } catch (error: any) {
            console.error('Failed to load team details:', error);
            toast.error(error.message || 'Failed to load team details');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateTeam = async () => {
        if (!id || !teamName.trim()) {
            toast.error('Team name is required');
            return;
        }

        try {
            setUpdatingTeam(true);
            await teamService.updateTeam(id, {
                name: teamName.trim(),
                description: teamDescription.trim() || undefined,
            });
            toast.success('Team updated successfully');
            setEditTeamOpen(false);
            await loadTeamDetails();
        } catch (error: any) {
            console.error('Failed to update team:', error);
            toast.error(error.message || 'Failed to update team');
        } finally {
            setUpdatingTeam(false);
        }
    };

    const handleInviteMember = async () => {
        if (!id || !inviteEmail.trim()) {
            toast.error('Email is required');
            return;
        }

        try {
            setInviting(true);
            await teamService.inviteTeamMember(id, {
                email: inviteEmail.trim(),
                role: inviteRole,
            });
            toast.success('Invitation sent successfully');
            setInviteOpen(false);
            setInviteEmail('');
            setInviteRole('member');
        } catch (error: any) {
            console.error('Failed to invite member:', error);
            toast.error(error.message || 'Failed to send invitation');
        } finally {
            setInviting(false);
        }
    };

    const handleRemoveMember = async (memberId: string, memberName: string) => {
        if (!id) return;


        try {
            // Optimistic UI: remove locally first
            setTeamDetails((prev) => prev ? { ...prev, members: prev.members.filter(m => m.userId !== memberId) } : prev);
            await teamService.removeTeamMember(id, memberId);
            toast.success(`${memberName} has been removed from the team`);
        } catch (error: any) {
            console.error('Failed to remove member:', error);
            toast.error(error.message || 'Failed to remove member');
            // Revert on error by reloading members
            await loadTeamDetails();
        }
    };

    const handleUpdateMemberRole = async (memberId: string, newRole: TeamMemberRole, memberName: string) => {
        if (!id) return;

        // Prevent self role change via UI safeguard
        if (user?.id === memberId) {
            toast.error("You can't change your own role");
            return;
        }

        // Optimistic UI: update locally, then call API; revert if fails
        const prevMembers = teamDetails?.members || [];
        const prevMember = prevMembers.find(m => m.userId === memberId);
        const prevRole = prevMember?.role;

        try {
            setTeamDetails((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    members: prev.members.map(m => m.userId === memberId ? { ...m, role: newRole } : m),
                };
            });
            await teamService.updateTeamMemberRole(id, memberId, newRole);
            toast.success(`${memberName}'s role has been updated to ${newRole}`);
        } catch (error: any) {
            console.error('Failed to update member role:', error);
            toast.error(error.message || 'Failed to update member role');
            // Revert role on error
            if (prevRole) {
                setTeamDetails((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        members: prev.members.map(m => m.userId === memberId ? { ...m, role: prevRole } : m),
                    };
                });
            }
        }
    };

    if (loading) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                    <div className="space-y-6">
                        <Skeleton className="h-8 w-48" />
                        <div className="grid gap-6">
                            <Skeleton className="h-48 rounded-xl" />
                            <Skeleton className="h-64 rounded-xl" />
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!teamDetails || (userRole !== 'owner' && userRole !== 'admin')) {
        return (
            <PageLayout nav={nav} footer={footer}>
                <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                    <div className="text-center py-12">
                        <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
                        <p className="text-muted-foreground mb-6">
                            You don't have permission to manage this team.
                        </p>
                        <Button asChild>
                            <Link to="/app/teams">
                                <ArrowLeft />
                                Back to Team
                            </Link>
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const { team, members } = teamDetails;
    const isOwner = userRole === 'owner';
    const canModifyMembers = userRole === 'owner' || userRole === 'admin';

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-6xl w-full">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Button variant="outline" size="sm" asChild>
                            <Link to={`/app/teams/${id}`}>
                                <ArrowLeft />
                                Back
                            </Link>
                        </Button>
                        <div>
                            <h1 className="text-3xl font-bold tracking-tight">Manage {team.name}</h1>
                            <p className="text-muted-foreground">Team administration and member management</p>
                        </div>
                    </div>
                </div>

                <div className="grid gap-6">
                    {/* Team Settings */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Settings className="size-5" />
                                        Team Settings
                                    </CardTitle>
                                    <CardDescription>
                                        {isOwner ? 'Update team information and settings' : 'View team information'}
                                    </CardDescription>
                                </div>
                                {isOwner && (
                                    <EditTeamDialog
                                        open={editTeamOpen}
                                        onOpenChange={setEditTeamOpen}
                                        teamName={teamName}
                                        setTeamName={setTeamName}
                                        teamDescription={teamDescription}
                                        setTeamDescription={setTeamDescription}
                                        updatingTeam={updatingTeam}
                                        onUpdate={handleUpdateTeam}
                                    />
                                )}
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                <div>
                                    <h4 className="font-medium mb-1">Name</h4>
                                    <p className="text-sm text-muted-foreground">{team.name}</p>
                                </div>
                                <div>
                                    <h4 className="font-medium mb-1">Slug</h4>
                                    <p className="text-sm text-muted-foreground">/{team.slug}</p>
                                </div>
                                <div className="md:col-span-2">
                                    <h4 className="font-medium mb-1">Description</h4>
                                    <p className="text-sm text-muted-foreground">
                                        {team.description || 'No description provided'}
                                    </p>
                                </div>
                                <div className="md:col-span-2">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-medium">Bring Your Own AI Key</h4>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <button className="text-muted-foreground hover:text-foreground" aria-label="About key mode">
                                                            <Info className="size-4" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p className="max-w-xs">
                                                            Team Key Mode determines which AI API key is used for session AI chats. Choose "Default" to use Arrowise's key, or "BYOK" to use the team owner's key.
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                        <div className="items-center">
                                            {(isOwner || userRole === 'admin') && (
                                                <UpdateTeamKeyModeDialog
                                                    open={keyModeDialogOpen}
                                                    onOpenChange={setKeyModeDialogOpen}
                                                    currentKeyMode={team.keyMode as TeamKeyMode}
                                                    isOwner={isOwner}
                                                    onUpdate={async (newMode) => {
                                                        if (!id) return;
                                                        await teamService.updateTeamKeyMode(id, newMode);
                                                        await loadTeamDetails();
                                                    }}
                                                    trigger={<Button size="sm" variant="outline">Change Key Mode</Button>}
                                                />
                                            )}
                                            <Badge className='ml-2' variant={team.keyMode === 'byok' ? 'default' : 'secondary'}>
                                                {team.keyMode === 'byok' ? 'BYOK' : 'Default'}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Team Members */}
                    <TeamMembersCard
                        members={members}
                        canManage={true}
                        canModify={canModifyMembers}
                        currentUserId={user?.id}
                        onInviteMember={() => setInviteOpen(true)}
                        onRoleChange={handleUpdateMemberRole}
                        onRemoveMember={handleRemoveMember}
                        showInviteButton={true}
                        showJoinDate={true}
                        showRemoveButton={true}
                        title={`Team Members (${members.length})`}
                        description="Manage team members and their roles"
                    />

                    {/* Invite Member Dialog */}
                    <InviteMemberDialog
                        open={inviteOpen}
                        onOpenChange={setInviteOpen}
                        email={inviteEmail}
                        onEmailChange={setInviteEmail}
                        role={inviteRole}
                        onRoleChange={setInviteRole}
                        isInviting={inviting}
                        onInvite={handleInviteMember}
                        isOwner={isOwner}
                    />
                </div>
            </div>
        </PageLayout>
    );
};

export default ManageTeamPage;
